import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, Archive, ArchiveRestore, Trash2, X, AlertTriangle } from 'lucide-react';
import { archiveLearningPath } from '@shared/services/learningPath';
import { deleteUserContent } from '@shared/services/content';
import { MomentumRing } from './MomentumRing';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';
import type { LearningPath } from '@shared/types/learning-path';
import type { ContentDocument } from '@shared/types/content';

interface LearningPathsListProps {
  paths: LearningPath[];
  content: ContentDocument[];
  onContinue: (contentId: string) => void;
  onStart: (contentId: string) => void;
  /** Optional callback — the dashboard can refetch content+paths on delete. */
  onContentDeleted?: (contentId: string) => void;
}

type Tab = 'active' | 'completed' | 'archived';

interface DeleteTarget {
  contentId: string;
  contentTitle: string;
  pathCount: number;
}

export function LearningPathsList({
  paths, content, onContinue, onStart, onContentDeleted,
}: LearningPathsListProps) {
  const [activeTab, setActiveTab] = useState<Tab>('active');
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [menuOpenPathId, setMenuOpenPathId] = useState<string | null>(null);
  const [busyPathId, setBusyPathId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const archivedPaths = paths.filter((p) => p.archived === true);
  const livePaths = paths.filter((p) => p.archived !== true);
  const completePaths = livePaths.filter((p) => {
    if (p.generationStatus !== 'complete') return false;
    return p.nodes.length > 0 && p.progress.completedNodes.length >= p.nodes.length;
  });
  const activePaths = livePaths.filter((p) => !completePaths.includes(p));

  const displayPaths =
    activeTab === 'active' ? activePaths :
    activeTab === 'completed' ? completePaths :
    archivedPaths;

  async function toggleArchive(path: LearningPath) {
    setBusyPathId(path.pathId);
    try {
      await archiveLearningPath(path.pathId, !path.archived);
    } catch (err) {
      console.error('Failed to toggle archive:', err);
    } finally {
      setBusyPathId(null);
      setMenuOpenPathId(null);
    }
  }

  function openDelete(path: LearningPath) {
    const doc = content.find((c) => c.contentId === path.contentId);
    const pathCount = paths.filter((p) => p.contentId === path.contentId).length;
    setDeleteTarget({
      contentId: path.contentId,
      contentTitle: doc?.title ?? 'this content',
      pathCount,
    });
    setMenuOpenPathId(null);
  }

  return (
    <div className={`${THICK_TILE_CLASSNAME} p-5 !overflow-visible`} style={THICK_TILE_STYLE}>
      {/* Header + tabs */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-heading text-lg font-bold m-0">Learning Paths</h2>
        <div className="flex gap-0.5 bg-dark-surface rounded-lg p-0.5">
          {(['active', 'completed', 'archived'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-md text-xs font-medium cursor-pointer font-body capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-indigo-500/8 text-indigo-500 border border-indigo-500/12'
                  : 'bg-transparent text-content-dim border border-transparent'
              }`}
              style={{ padding: '5px 12px' }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Path rows */}
      {displayPaths.map((path) => {
        const doc = content.find((c) => c.contentId === path.contentId);
        const totalNodes = path.nodes.length;
        const completedNodes = path.progress.completedNodes.length;
        const pct = totalNodes > 0 ? Math.round((completedNodes / totalNodes) * 100) : 0;
        const isDone = pct >= 100;
        const isReady = path.generationStatus === 'complete';
        const isGenerating = !isReady && path.generationStatus !== 'error';
        const isFailed = path.generationStatus === 'error';
        const isArchived = path.archived === true;

        return (
          <div
            key={path.pathId}
            onMouseEnter={() => setHoveredPath(path.pathId)}
            onMouseLeave={() => setHoveredPath(null)}
            className="flex items-center gap-3 cursor-pointer transition-all duration-200 mb-2 rounded-xl relative"
            style={{
              background: hoveredPath === path.pathId ? '#13172c' : '#111528',
              border: `1px solid ${hoveredPath === path.pathId ? '#6C5CE720' : '#1a1f3a'}`,
              padding: '15px 18px',
              opacity: isArchived ? 0.7 : 1,
            }}
          >
            {path.heroImageUrl && (
              <img
                src={path.heroImageUrl}
                alt=""
                loading="lazy"
                className="w-11 h-11 rounded-lg object-cover shrink-0 border border-dark-border"
              />
            )}
            <MomentumRing done={completedNodes} total={totalNodes} size={42} />

            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-semibold text-white mb-0.5 truncate">
                {doc?.title ?? 'Learning path'}
              </div>
              <div className="text-xs text-content-dim">
                {isReady && `${completedNodes} of ${totalNodes} sections`}
                {isGenerating && <span className="text-indigo-400">Generating...</span>}
                {isFailed && <span className="text-error">Failed</span>}
                {isArchived && <span className="ml-2 text-content-dim">· archived</span>}
              </div>
            </div>

            <div className="text-right shrink-0">
              <div className={`text-[13px] font-semibold ${isDone ? 'text-success' : 'text-content'}`}>
                {isDone ? 'Completed' : isReady ? `${pct}%` : ''}
              </div>
            </div>

            {isReady && !isArchived && (
              <Link
                to={`/learn/${path.contentId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (pct > 0) onContinue(path.contentId);
                  else onStart(path.contentId);
                }}
                className="w-7 h-7 rounded-md flex items-center justify-center text-sm transition-transform duration-200"
                style={{
                  background: isDone ? '#55EFC408' : '#6C5CE708',
                  color: isDone ? '#55EFC4' : '#6C5CE7',
                  transform: hoveredPath === path.pathId ? 'translateX(2px)' : 'none',
                }}
              >
                {isDone ? '✓' : '→'}
              </Link>
            )}

            {/* Overflow menu */}
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpenPathId((current) => (current === path.pathId ? null : path.pathId));
                }}
                aria-label="Path options"
                disabled={busyPathId === path.pathId}
                className="w-7 h-7 rounded-md flex items-center justify-center text-content-dim hover:text-content hover:bg-dark-hover transition-colors cursor-pointer disabled:opacity-50"
              >
                <MoreVertical size={14} />
              </button>
              {menuOpenPathId === path.pathId && (
                <div
                  className="absolute right-0 top-full mt-1 z-20 w-48 rounded-lg border border-dark-border bg-dark-elevated shadow-modal overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                  onMouseLeave={() => setMenuOpenPathId(null)}
                >
                  <button
                    type="button"
                    onClick={() => toggleArchive(path)}
                    disabled={busyPathId === path.pathId}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-body text-content-secondary hover:text-content hover:bg-dark-hover transition-colors cursor-pointer"
                  >
                    {isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    {isArchived ? 'Restore from archive' : 'Archive'}
                  </button>
                  <div className="h-px bg-dark-border" />
                  <button
                    type="button"
                    onClick={() => openDelete(path)}
                    disabled={busyPathId === path.pathId}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-body text-[#FD79A8] hover:bg-[#FD79A8]/10 transition-colors cursor-pointer"
                  >
                    <Trash2 size={14} />
                    Delete content
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {displayPaths.length === 0 && (
        <div className="text-center" style={{ padding: 32 }}>
          {paths.length === 0 ? (
            <>
              <div className="text-sm font-body text-content-secondary mb-1">No learning paths yet</div>
              <div className="text-[12px] font-body text-content-dim mb-4">
                Upload a document to start your first personalised learning path.
              </div>
              <Link
                to="/upload"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body font-semibold bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 transition-colors"
              >
                Upload content
              </Link>
            </>
          ) : (
            <div className="text-content-dim text-[13px]">
              {activeTab === 'archived' ? 'No archived paths' : `No ${activeTab} paths`}
            </div>
          )}
        </div>
      )}

      {/* Type-to-confirm delete modal */}
      {deleteTarget && (
        <DeleteContentModal
          target={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onDeleted={(contentId) => {
            setDeleteTarget(null);
            onContentDeleted?.(contentId);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DeleteContentModal — type-to-confirm then call the Cloud Function
// ─────────────────────────────────────────────────────────────

const CONFIRM_WORD = 'DELETE';

function DeleteContentModal({
  target,
  onCancel,
  onDeleted,
}: {
  target: DeleteTarget;
  onCancel: () => void;
  onDeleted: (contentId: string) => void;
}) {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC to cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const canConfirm = input === CONFIRM_WORD && !busy;

  async function handleConfirm() {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await deleteUserContent(target.contentId);
      onDeleted(target.contentId);
    } catch (err) {
      console.error('deleteUserContent failed:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Delete failed — please try again or contact support.',
      );
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4, 7, 17, 0.72)', backdropFilter: 'blur(4px)' }}
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-dark-border overflow-hidden"
        style={{
          background:
            'linear-gradient(160deg, rgba(28,34,60,0.98) 0%, rgba(20,25,48,0.97) 40%, rgba(14,18,36,0.97) 80%, rgba(9,12,26,0.98) 100%)',
          boxShadow: [
            '0 28px 60px -18px rgba(0, 0, 0, 0.75)',
            '0 10px 22px -6px rgba(0, 0, 0, 0.55)',
            'inset 0 1px 0 rgba(255, 255, 255, 0.07)',
            'inset 0 -1px 0 rgba(0, 0, 0, 0.55)',
          ].join(', '),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 border-b border-dark-border">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#FD79A8]/15 border border-[#FD79A8]/30 flex items-center justify-center">
              <AlertTriangle size={16} className="text-[#FD79A8]" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-[17px] m-0">Delete content</h3>
              <p className="text-xs text-content-muted mt-1">
                This can't be undone.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-content-dim hover:text-content transition-colors disabled:opacity-50 shrink-0"
            aria-label="Cancel"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-content-secondary">
            You're about to permanently delete{' '}
            <span className="text-content font-semibold">"{target.contentTitle}"</span>
            {target.pathCount > 0 && (
              <>
                {' '}and {target.pathCount === 1
                  ? 'the learning path derived from it'
                  : `the ${target.pathCount} learning paths derived from it`}
              </>
            )}. This also removes any tutor sessions and uploaded files for this content.
          </p>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-content-dim mb-1.5">
              Type <span className="text-content font-mono font-semibold">{CONFIRM_WORD}</span> to confirm
            </label>
            <input
              type="text"
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
              className="w-full bg-dark-subtle border border-dark-border rounded-lg px-3 py-2 text-sm text-content font-mono focus:border-[#FD79A8]/50 focus:outline-none transition-colors disabled:opacity-50"
              placeholder={CONFIRM_WORD}
            />
          </div>

          {error && (
            <div className="text-xs text-[#FD79A8] bg-[#FD79A8]/10 border border-[#FD79A8]/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-dark-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-sm font-medium text-content-muted hover:text-content px-4 py-2 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-lg ${
              canConfirm
                ? 'bg-[#FD79A8] hover:bg-[#fc6ba0] text-white shadow-[#FD79A8]/25'
                : 'bg-dark-subtle text-content-dim cursor-not-allowed'
            }`}
          >
            {busy ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 size={14} />
                Delete forever
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
